import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  Eye,
  EyeOff,
  Search,
  ShieldCheck,
} from "lucide-react";
import {
  isApiConfigured,
  patchAuthAccountMobile,
  postSignin,
  postSignup,
  postVerifyTurnstileToken,
} from "../../apiClient";
import { buildAuthHrefWithNext, parseSafeInternalNextPath } from "../../navigateAfterAuth";
import TurnstileField from "../../components/TurnstileField";
import {
  isTurnstileEnabled,
  TURNSTILE_REQUIRED_MESSAGE,
  formatTurnstileErrorForUser,
} from "../../turnstileConfig";
import SiteLogo from "../../components/SiteLogo";
import PageSeo from "../../components/PageSeo";
import old1_PublicSiteFooter from "../components/old1_PublicSiteFooter";
import {
  COUNTRY_DIAL_OPTIONS,
  getCountryIsoFromDial,
  getDialCodeForCountryIso,
} from "../../countryDialOptions";
/** Align with API `users` columns and sensible UX caps (email VARCHAR(320), etc.). */
const SIGNUP_INPUT_MAX = {
  fullName: 200,
  email: 320,
  mobile: 24,
  companyName: 200,
  companyWebsite: 2048,
  password: 128,
};

const COMPANY_WEBSITE_PREFIX = "http://";

function stripLeadingUrlScheme(raw) {
  return String(raw ?? "").trim().replace(/^https?:\/\//i, "");
}

/** Scheme-less URL sent to API / navigation state (prefix shown outside the input). */
function buildCompanyWebsiteForSubmit(remainder) {
  const body = stripLeadingUrlScheme(remainder).trim();
  return body || "";
}

const COMPANY_WEBSITE_INPUT_MAX = SIGNUP_INPUT_MAX.companyWebsite;

function old1_SignupPage({ onSignIn, currentUser, authToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authBase = "/old1";
  const typeParam = searchParams.get("type");
  const safeNext = useMemo(() => parseSafeInternalNextPath(searchParams.get("next")), [searchParams]);
  const signinHref = useMemo(() => buildAuthHrefWithNext(`${authBase}/signin`, safeNext), [authBase, safeNext]);
  const verifyMobilePath = useMemo(
    () => (safeNext ? `/old1/verify-mobile?next=${encodeURIComponent(safeNext)}` : "/old1/verify-mobile"),
    [safeNext]
  );
  const stateSignupData = location.state?.signupData;
  /** After email verification, user may return here only to fix mobile — email stays fixed. */
  const emailLocked = Boolean(stateSignupData?.emailVerified);
  const allowedSignupTypes = ["applicant", "hiring-manager"];
  const validType =
    typeParam === "hiring-manager" || typeParam === "recruiter"
      ? "hiring-manager"
      : "applicant";
  const stateType = stateSignupData?.signupType;
  const normalizedStateType =
    stateType === "recruiter" ? "hiring-manager" : stateType;
  const initialSignupType = allowedSignupTypes.includes(normalizedStateType)
    ? normalizedStateType
    : validType;
  const [signupType, setSignupType] = useState(initialSignupType);
  const [fullName, setFullName] = useState(stateSignupData?.fullName || "");
  const [email, setEmail] = useState(stateSignupData?.email || "");
  const [countryIso, setCountryIso] = useState(() => {
    const s = stateSignupData;
    if (!s) return "GB";
    if (typeof s.countryIso === "string" && /^[A-Za-z]{2}$/.test(s.countryIso.trim())) {
      return s.countryIso.trim().toUpperCase();
    }
    return getCountryIsoFromDial(s.countryCode) || "GB";
  });
  const [mobile, setMobile] = useState(stateSignupData?.mobile || "");
  const [companyName, setCompanyName] = useState(stateSignupData?.companyName || "");
  const [companyWebsite, setCompanyWebsite] = useState(() =>
    stripLeadingUrlScheme(stateSignupData?.companyWebsite || "")
  );
  const [password, setPassword] = useState(stateSignupData?.password || "");
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [acceptedLegal, setAcceptedLegal] = useState(Boolean(stateSignupData?.acceptedLegal));

  /** Cloudflare edge country when no signupData from navigation (e.g. fresh signup). */
  useEffect(() => {
    if (stateSignupData) return;
    let cancelled = false;
    (async () => {
      const iso = await fetchSuggestedSignupCountryIso();
      if (!cancelled && iso) setCountryIso(iso);
    })();
    return () => {
      cancelled = true;
    };
  }, [stateSignupData]);

  const needsCompanyFields = signupType === "hiring-manager";

  const blockedDomains = useMemo(
    () => [
      "gmail.com",
      "hotmail.com",
      "outlook.com",
      "yahoo.com",
      "icloud.com",
      "aol.com",
      "proton.me",
      "protonmail.com",
    ],
    []
  );

  const hasPublicEmailDomain = (value) => {
    const parts = value.toLowerCase().split("@");
    if (parts.length !== 2) return false;
    return blockedDomains.includes(parts[1]);
  };

  const isBusinessEmailValid = (value) => {
    if (!needsCompanyFields) return true;
    if (!value) return false;
    return !hasPublicEmailDomain(value);
  };

  let title = "Sign up as Applicant";
  let subtitle = "Complete the form to continue.";

  if (signupType === "applicant") {
    subtitle = "Find real opportunities that match your skills.";
  } else if (signupType === "hiring-manager") {
    title = "Sign up as Hiring Manager";
    subtitle = "Manage openings and streamline your hiring process.";
  }

  const onEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);

    if (needsCompanyFields && value && hasPublicEmailDomain(value)) {
      event.target.setCustomValidity(
        "Use your company email address. Public domains are not allowed for this sign up type."
      );
    } else {
      event.target.setCustomValidity("");
    }
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const emailInput = form.elements.email;
    const normalizedEmail = email.trim().toLowerCase();
    setSubmitError("");

    if (!emailLocked && needsCompanyFields && hasPublicEmailDomain(email)) {
      emailInput.setCustomValidity(
        "Use your company email address. Public domains are not allowed for this sign up type."
      );
    } else {
      emailInput.setCustomValidity("");
    }

    if (!emailLocked && !form.checkValidity()) {
      form.reportValidity();
      return;
    }

    if (emailLocked) {
      if (!String(mobile || "").trim()) {
        setSubmitError("Enter your mobile number.");
        return;
      }
      if (isApiConfigured()) {
        if (!String(authToken || "").trim()) {
          setSubmitError(
            "Your session expired. Open sign up again from the mobile verification page, or sign in."
          );
          return;
        }
        setSubmitting(true);
        try {
          const { user } = await patchAuthAccountMobile({
            countryCode: getDialCodeForCountryIso(countryIso),
            mobile,
            authToken,
          });
          onSignIn(user);
          navigate(verifyMobilePath, {
            state: {
              signupData: {
                signupType,
                fullName,
                email,
                countryCode: getDialCodeForCountryIso(countryIso),
                countryIso,
                mobile,
                companyName,
                companyWebsite: buildCompanyWebsiteForSubmit(companyWebsite),
                password,
                emailVerified: true,
              },
            },
          });
        } catch (err) {
          setSubmitError(err instanceof Error ? err.message : "Update failed");
        } finally {
          setSubmitting(false);
        }
        return;
      }
      navigate(verifyMobilePath, {
        state: {
          signupData: {
            signupType,
            fullName,
            email,
            countryCode: getDialCodeForCountryIso(countryIso),
            countryIso,
            mobile,
            companyName,
            companyWebsite: buildCompanyWebsiteForSubmit(companyWebsite),
            password,
            emailVerified: true,
          },
        },
      });
      return;
    }

    if (!emailLocked && !acceptedLegal) {
      setSubmitError("Please agree to the Terms of Use and Privacy Policy to continue.");
      return;
    }

    if (!emailLocked && isTurnstileEnabled() && isApiConfigured()) {
      if (!turnstileToken) {
        setSubmitError(TURNSTILE_REQUIRED_MESSAGE);
        return;
      }
      setSubmitting(true);
      try {
        await postVerifyTurnstileToken(turnstileToken);
      } catch (err) {
        const raw = err instanceof Error ? err.message : "Verification failed";
        setSubmitError(formatTurnstileErrorForUser(raw));
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }

    if (!emailLocked && isApiConfigured()) {
      setSubmitting(true);
      try {
        const result = await postSignup({
          email,
          password,
          fullName,
          signupType,
          countryCode: getDialCodeForCountryIso(countryIso),
          mobile,
          companyName: needsCompanyFields ? companyName : "",
          companyWebsite: needsCompanyFields ? buildCompanyWebsiteForSubmit(companyWebsite) : "",
          acceptTerms: acceptedLegal,
        });
        onSignIn(result.user, { token: result.token });
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : "Sign up failed");
        return;
      } finally {
        setSubmitting(false);
      }
    }

    const verifyEmailQs = new URLSearchParams();
    verifyEmailQs.set("email", email);
    if (safeNext) verifyEmailQs.set("next", safeNext);
    navigate(`/verify-email?${verifyEmailQs.toString()}`, {
      state: {
        signupData: {
          signupType,
          fullName,
          email,
          countryCode: getDialCodeForCountryIso(countryIso),
          countryIso,
          mobile,
          companyName,
          companyWebsite: needsCompanyFields ? buildCompanyWebsiteForSubmit(companyWebsite) : "",
          password,
        },
      },
    });
  };

  const mainContent = (
    <>
      <aside
        className={"jw-auth-aside jw-register-aside"
        }
        aria-label="Why create a JOBUWIN account"
      >
        <span className={"jw-public-chip"}>
          <ShieldCheck className={"jw-public-icon"} aria-hidden="true" />
          Genuine accounts only
        </span>
        <h1>Built for genuine people.</h1>
        <p>Communicate the real you, not just a polished CV or job description.</p>
        <div className={"jw-auth-benefits"}>
          <span>
            <CheckCircle2 className={"jw-public-icon"} aria-hidden="true" /> Designed to
            discourage fake profiles and low effort applications
          </span>
          <span>
            <CheckCircle2 className={"jw-public-icon"} aria-hidden="true" /> Encourage
            applicants and hiring managers to communicate honestly
          </span>
          <span>
            <CheckCircle2 className={"jw-public-icon"} aria-hidden="true" /> Push back against
            the growing AI driven hiring chaos
          </span>
        </div>
      </aside>

      <section
        className={"signup-card jw-public-card jw-auth-card jw-register-card"
        }
      >
        <Link className={"back-link jw-public-back-link"} to={"/home"}>
          <ArrowLeft className={"jw-public-icon"} aria-hidden="true" />
          Back to home
        </Link>
        <p className={"eyebrow jw-public-eyebrow"}>Create account</p>
        <h2>{title}</h2>
        <p className={"subtitle"}>{subtitle}</p>

          {submitError && submitError !== TURNSTILE_REQUIRED_MESSAGE ? (
            <div className="signup-submit-error-banner" role="alert">
              <p className="signup-submit-error-banner__text">{submitError}</p>
            </div>
          ) : null}

          <div className={`signup-type${emailLocked ? " signup-type--locked" : ""}`}>
            <p className="signup-type-title">I am...</p>
            <div className="signup-type-radios">
              <label className={`type-option type-option--applicant ${signupType === "applicant" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name="signupType"
                  value="applicant"
                  checked={signupType === "applicant"}
                  onChange={(event) => setSignupType(event.target.value)}
                  disabled={emailLocked}
                />
                <span className="jw-register-type-icon jw-register-type-icon--p1">
                  <Search className="jw-public-icon" aria-hidden="true" />
                </span>
                <span>Looking for a job</span>
              </label>
              <label className={`type-option type-option--hiring ${signupType === "hiring-manager" ? "selected" : ""}`}>
                <input
                  type="radio"
                  name="signupType"
                  value="hiring-manager"
                  checked={signupType === "hiring-manager"}
                  onChange={(event) => setSignupType(event.target.value)}
                  disabled={emailLocked}
                />
                <span className="jw-register-type-icon jw-register-type-icon--p2">
                  <BriefcaseBusiness className="jw-public-icon" aria-hidden="true" />
                </span>
                <span>Hiring for a Job(s)</span>
              </label>
            </div>
          </div>

          <form
            className={`signup-form${emailLocked ? " signup-form--email-locked" : ""}`}
            action="#"
            method="post"
            onSubmit={onSubmit}
          >
            <label htmlFor="fullName" className={emailLocked ? "signup-label-muted" : undefined}>
              Full name
            </label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              placeholder="Enter your name"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              maxLength={SIGNUP_INPUT_MAX.fullName}
              required
              readOnly={emailLocked}
              className={emailLocked ? "signup-input-readonly" : undefined}
            />

            <label htmlFor="email" className={emailLocked ? "signup-label-muted" : undefined}>
              Email address
            </label>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={onEmailChange}
              maxLength={SIGNUP_INPUT_MAX.email}
              required
              readOnly={emailLocked}
              className={emailLocked ? "signup-input-readonly" : undefined}
              aria-readonly={emailLocked}
            />
            {needsCompanyFields && !isBusinessEmailValid(email) && email ? (
              <p className="field-note">
                Please use a company email address (public email domains are not
                allowed).
              </p>
            ) : null}

            <label htmlFor="mobile">Mobile number</label>
            <div className={`mobile-row${emailLocked ? " mobile-row--editable" : ""}`}>
              <select
                id="countryIso"
                name="countryIso"
                value={countryIso}
                onChange={(event) => setCountryIso(event.target.value)}
                required
                aria-label="Country for mobile number"
                aria-describedby={emailLocked ? "signup-mobile-only-hint" : undefined}
              >
                {COUNTRY_DIAL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                placeholder="7123 456789"
                value={mobile}
                onChange={(event) => setMobile(event.target.value)}
                maxLength={SIGNUP_INPUT_MAX.mobile}
                required
                aria-describedby={emailLocked ? "signup-mobile-only-hint" : undefined}
              />
            </div>

            {needsCompanyFields ? (
              <>
                <label
                  htmlFor="companyName"
                  className={emailLocked ? "signup-label-muted" : undefined}
                >
                  Company name
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  placeholder="Enter company name"
                  value={companyName}
                  onChange={(event) => setCompanyName(event.target.value)}
                  maxLength={SIGNUP_INPUT_MAX.companyName}
                  required
                  readOnly={emailLocked}
                  className={emailLocked ? "signup-input-readonly" : undefined}
                />

                <label
                  htmlFor="companyWebsite"
                  className={emailLocked ? "signup-label-muted" : undefined}
                >
                  Company website
                </label>
                <div
                  className={`signup-url-field${emailLocked ? " signup-url-field--readonly" : ""}`}
                >
                  <span className="signup-url-prefix">{COMPANY_WEBSITE_PREFIX}</span>
                  <input
                    id="companyWebsite"
                    name="companyWebsite"
                    type="text"
                    inputMode="url"
                    autoComplete="url"
                    placeholder="www.company.com"
                    value={companyWebsite}
                    onChange={(event) => setCompanyWebsite(stripLeadingUrlScheme(event.target.value))}
                    maxLength={COMPANY_WEBSITE_INPUT_MAX}
                    required
                    readOnly={emailLocked}
                    className={emailLocked ? "signup-input-readonly" : undefined}
                  />
                </div>
              </>
            ) : null}

            {!emailLocked ? (
              <label htmlFor="password">Password</label>
            ) : (
              <p className="signup-password-optional-note" id="signup-mobile-only-hint">
                {
                  "You can update your mobile number and country code — when you're happy with them, continue to the next step."
                }
              </p>
            )}
            {!emailLocked ? (
            <div className="password-field jw-register-password-field">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                maxLength={SIGNUP_INPUT_MAX.password}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff className="jw-public-icon" aria-hidden="true" />
                ) : (
                  <Eye className="jw-public-icon" aria-hidden="true" />
                )}
              </button>
            </div>
            ) : null}

            {isTurnstileEnabled() && isApiConfigured() && !emailLocked ? (
              <>
                {submitError === TURNSTILE_REQUIRED_MESSAGE ? (
                  <p className="turnstile-inline-alert" role="alert">
                    {submitError}
                  </p>
                ) : null}
                <TurnstileField
                  onTokenChange={(token) => {
                    setTurnstileToken(token);
                    if (token) {
                      setSubmitError((prev) => (prev === TURNSTILE_REQUIRED_MESSAGE ? "" : prev));
                    }
                  }}
                  widgetKey="signup"
                />
                <p className="turnstile-human-note">
                  Security check: making sure you are a genuine human :-)
                </p>
              </>
            ) : null}

            {!emailLocked ? (
              <label className="signup-legal-consent">
                <input
                  type="checkbox"
                  name="acceptLegal"
                  checked={acceptedLegal}
                  onChange={(event) => {
                    setAcceptedLegal(event.target.checked);
                    if (event.target.checked) {
                      setSubmitError((prev) =>
                        prev === "Please agree to the Terms of Use and Privacy Policy to continue."
                          ? ""
                          : prev
                      );
                    }
                  }}
                />
                <span>
                  I agree to the{" "}
                  <Link to="/old1/terms" target="_blank" rel="noopener noreferrer">
                    Terms of Use
                  </Link>{" "}
                  and{" "}
                  <Link to="/old1/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
            ) : null}

            <button
              type="submit"
              className={"submit-button jw-public-button jw-public-button--primary"
              }
              disabled={submitting}
            >
              {submitting
                ? emailLocked
                  ? "Saving..."
                  : "Signing up..."
                : emailLocked
                  ? "Save mobile & continue"
                  : "Sign up"}
              <ArrowRight className={"jw-public-icon"} aria-hidden="true" />
            </button>
          </form>
        </section>
    </>
  );

  return (
    <div className="signup-page jw-public-page jw-auth-page jw-register-page">
      <PageSeo
        title="Create your account"
        description="Join JOBUWIN to find genuine jobs, hire real people, and connect through authentic hiring conversations."
        path="/old1/signup"
        noindex
      />
      <nav className="landing-nav jw-public-nav" aria-label="Registration navigation">
        <div className="landing-nav-shell jw-public-nav__shell">
          <SiteLogo />
          <div className="nav-buttons landing-nav-actions">
            <button
              type="button"
              className="jw-public-button jw-public-button--text landing-nav-login"
              onClick={() => navigate(signinHref)}
            >
              Sign in
            </button>
            <button
              type="button"
              className="jw-public-button jw-public-button--primary nav-button-current landing-nav-cta"
              aria-current="page"
            >
              Sign up
              <ArrowRight className="jw-public-icon" aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>

      <main id="main-content" className="signup-container jw-auth-shell jw-auth-shell--signup">
        {mainContent}
      </main>

      <old1_PublicSiteFooter />
    </div>
  );
}

export default old1_SignupPage;
